import { useEffect, useMemo, useState } from "react";
import {
  Drawer,
  Checkbox,
  Select,
  Form,
  Radio,
  DatePicker,
  Modal,
  Slider,
  Input,
} from "antd";
import PrimaryButton from "./Forms/PrimaryButton";
import { useApiCall } from "../services/api";
import { CloseCircleOutlined } from "@ant-design/icons";
import { useLocation } from "react-router-dom";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

const Filters = ({
  title = "Filters",
  width = 400,
  filters = [],
  onChange,
  buttonLabel = "Filters",
  persistToUrl = true,
}) => {
  const { postApi, getApi, loading } = useApiCall();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [finalFilters, setFinalFilters] = useState(filters || []);
  const [appliedFilters, setAppliedFilters] = useState({});
  const [form] = Form.useForm();
  const stableFilters = useMemo(
    () => filters,
    [JSON.stringify(filters.map((f) => ({ key: f.key, options: f.options })))],
  );
  const storageKey = useMemo(() => {
    const keys = stableFilters.map((f) => f.key).join("|");
    return `filters:${location.pathname}:${keys}`;
  }, [location.pathname, stableFilters]);

  const serializeFiltersForStorage = (filterValues) => {
    const payload = {};

    Object.entries(filterValues || {}).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") {
        return;
      }

      const filterConfig = stableFilters.find((f) => f.key === key);
      if (!filterConfig) return;

      if (
        filterConfig.type === "daterange" &&
        Array.isArray(value) &&
        value.length === 2
      ) {
        payload[key] = [
          value[0]?.format?.("YYYY-MM-DD") || value[0],
          value[1]?.format?.("YYYY-MM-DD") || value[1],
        ];
      } else if (filterConfig.type === "date") {
        payload[key] = value?.format?.("YYYY-MM-DD") || value;
      } else if (filterConfig.type === "range" && Array.isArray(value)) {
        payload[key] = value;
      } else if (filterConfig.type === "checkboxes" && Array.isArray(value)) {
        payload[key] = value;
      } else {
        payload[key] = value;
      }
    });

    return payload;
  };

  const deserializeFiltersFromStorage = () => {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return {};

    let savedFilters = {};
    try {
      savedFilters = JSON.parse(raw);
    } catch (error) {
      console.error("Error parsing saved filters:", error);
      return {};
    }

    const restored = {};
    stableFilters.forEach((f) => {
      const value = savedFilters[f.key];
      if (value === null || value === undefined || value === "") return;

      if (
        f.type === "daterange" &&
        Array.isArray(value) &&
        value.length === 2
      ) {
        restored[f.key] = [dayjs(value[0]), dayjs(value[1])];
      } else if (f.type === "date") {
        restored[f.key] = dayjs(value);
      } else {
        restored[f.key] = value;
      }
    });

    return restored;
  };

  const showDrawer = () => setOpen(true);
  const hideDrawer = () => setOpen(false);

  const handleApply = () => {
    form.validateFields().then((values) => {
      setAppliedFilters(values);
      if (persistToUrl) {
        const serialized = serializeFiltersForStorage(values);
        sessionStorage.setItem(storageKey, JSON.stringify(serialized));
      }
      onChange?.(values);
      hideDrawer();
    });
  };

  const handleReset = () => {
    form.resetFields();
    setAppliedFilters({});
    if (persistToUrl) {
      sessionStorage.removeItem(storageKey);
    }
    onChange?.({});
    hideDrawer();
  };

  const hasFilters = Object.values(appliedFilters).some(
    (val) => val !== null && val !== undefined,
  );

  useEffect(() => {
    if (persistToUrl) {
      const restoredFilters = deserializeFiltersFromStorage();
      if (Object.keys(restoredFilters).length > 0) {
        form.setFieldsValue(restoredFilters);
        setAppliedFilters(restoredFilters);
      }
    }
  }, [persistToUrl, storageKey]);

  const FilterButton = (props) => (
    <PrimaryButton
      btnType={hasFilters ? "primary" : "secondary"}
      text={buttonLabel}
      onClick={showDrawer}
      verified={hasFilters}
      noIcon
      bgClassName="!bg-white"
      loading={loading}
      {...props}
    />
  );

  const FooterUI = () => (
    <div className="flex justify-between">
      <PrimaryButton
        btnType="secondary"
        onClick={handleReset}
        text="Reset"
        className="!h-[35px]"
        textClassName="text-sm"
        loading={loading}
      />
      <PrimaryButton
        type="primary"
        noIcon
        onClick={handleApply}
        text="Apply"
        className="!h-[35px]"
        textClassName="text-sm"
        loading={loading}
      />
    </div>
  );

  const FiltersUI = () => {
    // Only used for the new search-select types
    const [searchTexts, setSearchTexts] = useState({});
    const [openSearchSelects, setOpenSearchSelects] = useState({});

    return (
      <Form layout="vertical" form={form}>
        {finalFilters?.map((filter) => {
          const {
            type,
            key,
            label,
            options = [],
            formProps,
            inputProps,
            isDisabled,
            defaultValue,
            className,
          } = filter;

          switch (type) {
            case "input":
              return (
                <Form.Item
                  key={key}
                  name={key}
                  label={label}
                  initialValue={defaultValue}
                  {...formProps}
                >
                  <Input
                    placeholder={`Select ${label}`}
                    disabled={isDisabled}
                    style={{ paddingInlineStart: 12, paddingInlineEnd: 12 }}
                    {...inputProps}
                  />
                </Form.Item>
              );

            case "checkboxes":
              return (
                <Form.Item
                  key={key}
                  name={key}
                  label={label}
                  initialValue={defaultValue}
                  {...formProps}
                >
                  <Checkbox.Group
                    className="custom-checkbox"
                    options={options}
                    disabled={isDisabled}
                    {...inputProps}
                  />
                </Form.Item>
              );

            case "checkbox":
              return (
                <div
                  key={key}
                  className={`flex justify-between items-center mb-[24px] ${className || ""}`}
                >
                  <label className="font-medium">{label}</label>
                  <Form.Item
                    name={key}
                    valuePropName="checked"
                    initialValue={defaultValue}
                    {...formProps}
                    className="mb-0"
                  >
                    <Checkbox
                      className="custom-checkbox"
                      disabled={isDisabled}
                      {...inputProps}
                    />
                  </Form.Item>
                </div>
              );

            // ==========================================
            // STANDARD NATIVE SELECTS
            // ==========================================
            case "select":
              return (
                <Form.Item
                  key={key}
                  name={key}
                  label={label}
                  initialValue={defaultValue}
                  {...formProps}
                >
                  <Select
                    showSearch
                    optionFilterProp="children"
                    placeholder={`Select ${label}`}
                    className="custom-dropdown"
                    classNames={{ popup: { root: "custom-dropdown-menu" } }}
                    disabled={isDisabled}
                    allowClear
                    {...inputProps}
                  >
                    {options.map((opt) => (
                      <Select.Option key={opt.value} value={opt.value}>
                        {opt.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              );

            case "multiselect":
              return (
                <Form.Item
                  key={key}
                  name={key}
                  label={label}
                  initialValue={defaultValue}
                  {...formProps}
                >
                  <Select
                    showSearch
                    optionFilterProp="children"
                    mode="multiple"
                    placeholder={`Select ${label}`}
                    className="custom-dropdown custom-multi-dropdown"
                    classNames={{ popup: { root: "custom-dropdown-menu" } }}
                    disabled={isDisabled}
                    allowClear
                    {...inputProps}
                  >
                    {options.map((opt) => (
                      <Select.Option key={opt.value} value={opt.value}>
                        {opt.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              );

            // ==========================================
            // CUSTOM SEARCH-TO-EXPAND SELECTS
            // ==========================================
            case "search-select": {
              const currentSearch = searchTexts[key] || "";
              const isOpen = !!openSearchSelects[key] && currentSearch.length > 0;

              return (
                <Form.Item
                  key={key}
                  name={key}
                  label={label}
                  initialValue={defaultValue}
                  {...formProps}
                >
                  <Select
                    showSearch
                    placeholder={`Select ${label}`}
                    className="custom-dropdown"
                    classNames={{ popup: { root: "custom-dropdown-menu" } }}
                    disabled={isDisabled}
                    allowClear
                    {...inputProps}
                    options={options}
                    open={isOpen}
                    getPopupContainer={(trigger) => trigger.parentElement}
                    onFocus={(e) => {
                      setOpenSearchSelects((prev) => ({ ...prev, [key]: true }));
                      if (inputProps?.onFocus) inputProps.onFocus(e);
                    }}
                    filterOption={(input, option) => {
                      if (input.length < 1) return false;
                      return String(option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase());
                    }}
                    onSearch={(val) => {
                      const nextValue = val || "";
                      setSearchTexts((prev) => ({ ...prev, [key]: nextValue }));
                      setOpenSearchSelects((prev) => ({ ...prev, [key]: nextValue.length > 0 }));
                      if (inputProps?.onSearch) inputProps.onSearch(val);
                    }}
                    onBlur={(e) => {
                      setSearchTexts((prev) => ({ ...prev, [key]: "" }));
                      setOpenSearchSelects((prev) => ({ ...prev, [key]: false }));
                      if (inputProps?.onBlur) inputProps.onBlur(e);
                    }}
                    onSelect={(val, opt) => {
                      setSearchTexts((prev) => ({ ...prev, [key]: "" }));
                      setOpenSearchSelects((prev) => ({ ...prev, [key]: false }));
                      if (inputProps?.onSelect) inputProps.onSelect(val, opt);
                    }}
                  />
                </Form.Item>
              );
            }

            case "search-multiselect": {
              const currentSearch = searchTexts[key] || "";
              const isOpen = !!openSearchSelects[key] && currentSearch.length > 0;

              return (
                <Form.Item
                  key={key}
                  name={key}
                  label={label}
                  initialValue={defaultValue}
                  {...formProps}
                >
                  <Select
                    showSearch
                    mode="multiple"
                    placeholder={`Select ${label}`}
                    className="custom-dropdown custom-multi-dropdown"
                    classNames={{ popup: { root: "custom-dropdown-menu" } }}
                    disabled={isDisabled}
                    allowClear
                    {...inputProps}
                    options={options}
                    open={isOpen}
                    getPopupContainer={(trigger) => trigger.parentElement}
                    onFocus={(e) => {
                      setOpenSearchSelects((prev) => ({ ...prev, [key]: true }));
                      if (inputProps?.onFocus) inputProps.onFocus(e);
                    }}
                    filterOption={(input, option) => {
                      if (input.length < 1) return false;
                      return String(option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase());
                    }}
                    onSearch={(val) => {
                      const nextValue = val || "";
                      setSearchTexts((prev) => ({ ...prev, [key]: nextValue }));
                      setOpenSearchSelects((prev) => ({ ...prev, [key]: nextValue.length > 0 }));
                      if (inputProps?.onSearch) inputProps.onSearch(val);
                    }}
                    onBlur={(e) => {
                      setSearchTexts((prev) => ({ ...prev, [key]: "" }));
                      setOpenSearchSelects((prev) => ({ ...prev, [key]: false }));
                      if (inputProps?.onBlur) inputProps.onBlur(e);
                    }}
                    onSelect={(val, opt) => {
                      setSearchTexts((prev) => ({ ...prev, [key]: "" }));
                      setOpenSearchSelects((prev) => ({ ...prev, [key]: false }));
                      if (inputProps?.onSelect) inputProps.onSelect(val, opt);
                    }}
                  />
                </Form.Item>
              );
            }

            // ==========================================
            // OTHER NATIVE COMPONENTS
            // ==========================================
            case "radio":
              return (
                <Form.Item
                  key={key}
                  name={key}
                  label={label}
                  initialValue={defaultValue}
                  {...formProps}
                >
                  <Radio.Group disabled={isDisabled} {...inputProps}>
                    {options.map((opt) => (
                      <Radio key={opt.value} value={opt.value}>
                        {opt.label}
                      </Radio>
                    ))}
                  </Radio.Group>
                </Form.Item>
              );

            case "date":
              return (
                <Form.Item
                  key={key}
                  name={key}
                  label={label}
                  initialValue={defaultValue}
                  {...formProps}
                >
                  <DatePicker
                    className="w-full"
                    disabled={isDisabled}
                    {...inputProps}
                  />
                </Form.Item>
              );

            case "daterange":
              return (
                <Form.Item
                  key={key}
                  name={key}
                  label={label}
                  initialValue={defaultValue}
                  {...formProps}
                >
                  <RangePicker
                    className="w-full"
                    disabled={isDisabled}
                    {...inputProps}
                  />
                </Form.Item>
              );

            case "range":
              return (
                <Form.Item
                  key={key}
                  shouldUpdate={(prev, curr) => prev[key] !== curr[key]}
                >
                  {({ getFieldValue, setFieldValue }) => {
                    const rangeValue = getFieldValue(key);
                    return (
                      <Form.Item
                        className="remove-margin"
                        name={key}
                        label={
                          <div className="pt-2 flex gap-2 justify-between items-center">
                            <span>{label}</span>
                            {Array.isArray(rangeValue) &&
                              rangeValue.some(
                                (v) => v !== null && v !== undefined,
                              ) && (
                                <CloseCircleOutlined
                                  onClick={() =>
                                    setFieldValue(
                                      key,
                                      filter.defaultValue ?? null,
                                    )
                                  }
                                  className="text-gray-400 hover:text-red-500 cursor-pointer"
                                />
                              )}
                          </div>
                        }
                        initialValue={filter.defaultValue ?? null}
                        {...formProps}
                      >
                        <Slider
                          range
                          min={filter.min ?? 0}
                          max={filter.max ?? 100}
                          step={filter.step ?? 1}
                          marks={filter.marks}
                          tooltip={{
                            formatter: filter.formatter || ((val) => `${val}`),
                          }}
                          disabled={isDisabled}
                          {...inputProps}
                        />
                      </Form.Item>
                    );
                  }}
                </Form.Item>
              );

            default:
              return null;
          }
        })}
      </Form>
    );
  };

  const DrawerUI = (props) => (
    <Drawer
      title={<h1 className="font-abhaya text-3xl">{title}</h1>}
      placement="right"
      onClose={hideDrawer}
      open={open}
      width={width}
      className="custom-filters"
      footer={<FooterUI />}
      {...props}
    >
      <FiltersUI />
    </Drawer>
  );

  const ModalUI = (props) => (
    <Modal
      title={<p className="font-abhaya text-3xl bg-transparent">{title}</p>}
      open={open}
      width={width}
      onCancel={hideDrawer}
      className="form-css"
      footer={<FooterUI />}
      {...props}
    >
      <FiltersUI />
    </Modal>
  );

  useEffect(() => {
    const fetchFilterOptions = async () => {
      const updatedFilters = await Promise.all(
        stableFilters.map(async (filter) => {
          if (filter.apiConfig && !filter.isDisabled) {
            const {
              apiRoute,
              body,
              handler,
              method = "POST",
            } = filter.apiConfig;
            const { options = [] } = filter;
            try {
              const response =
                method === "POST"
                  ? await postApi(apiRoute, body)
                  : await getApi(apiRoute);
              const newOptions = handler
                ? await handler(response)
                : response.data;
              return { ...filter, options: [...options, ...newOptions] };
            } catch (error) {
              console.error("Error fetching filter options:", error);
              return filter;
            }
          }
          return filter;
        }),
      );
      setFinalFilters(updatedFilters);
    };

    fetchFilterOptions();
  }, [stableFilters]);

  return {
    showDrawer,
    hideDrawer,
    appliedFilters,
    DrawerUI,
    FilterButton,
    ModalUI,
    resetFilters: handleReset,
    hasFilters,
  };
};

export default Filters;
